import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

function AnimatedBlock({
  children,
  delay = 0,
  direction = 'up',
}: {
  children: React.ReactNode;
  delay?: number;
  direction?: 'up' | 'left' | 'right';
}) {
  const { ref, visible } = useScrollAnimation(0.1);
  const translateMap = { up: 'translateY(40px)', left: 'translateX(-40px)', right: 'translateX(40px)' };

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate(0,0)' : translateMap[direction],
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function VisionSection() {
  const { ref: titleRef, visible: titleVisible } = useScrollAnimation(0.2);
  const { ref: closingRef, visible: closingVisible } = useScrollAnimation(0.2);

  const products = [
    {
      icon: 'ri-recycle-line',
      title: 're:day',
      subtitle: '지속가능한 패션',
      desc: '버려지는 자원을 다시 디자인하고 더 오래 사용할 수 있는 제품을 만드는 브랜드',
      color: 'emerald',
      img: 'https://public.readdy.ai/ai/img_res/4a309a54f2bd6a68d8bdc066f4dfd6eb.jpg',
    },
    {
      icon: 'ri-shield-line',
      title: '기능성 워크웨어',
      subtitle: '에어로젤 코팅 섬유',
      desc: '초경량 단열 소재를 활용해 극한 환경에서도 사람을 보호하는 기능성 워크웨어',
      color: 'amber',
      img: 'https://readdy.ai/api/search-image?query=Korean%20worker%20wearing%20functional%20workwear%20protective%20clothing%20industrial%20safety%20gear%20modern%20design%20lightweight%20insulation%20material%20professional%20Korean%20worker%20equipment%20clean%20studio%20photography%20neutral%20background%20Korea&width=600&height=700&seq=workwear-product-korean-v1&orientation=portrait',
    },
  ];

  const beliefs = [
    { icon: 'ri-user-line', text: '기술은 단순한 생산 수단이 아니라 사람의 삶을 바꾸는 힘' },
    { icon: 'ri-arrow-up-circle-line', text: '기술은 사람을 자립하게 하고, 자립은 사람을 존중받게 한다' },
    { icon: 'ri-leaf-line', text: '그 존중이 모일 때 비로소 지속가능한 산업이 만들어진다' },
  ];

  return (
    <section className="py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto">

        {/* Chapter 03 - 지속가능한 산업 */}
        <div
          ref={titleRef}
          className="mb-24"
          style={{
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.9s ease',
          }}
        >
          <span className="text-emerald-600 text-sm font-semibold tracking-widest uppercase">Chapter 03</span>
          <h2
            className="text-5xl font-bold text-stone-800 mt-3 mb-6 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            기술로 만드는
            <br />
            지속가능한 산업
          </h2>
          <div className="w-16 h-0.5 bg-emerald-500"></div>
        </div>

        {/* 제품 2개 카드 */}
        <div className="grid md:grid-cols-2 gap-10 mb-32">
          {products.map((p, i) => (
            <AnimatedBlock key={i} direction={i === 0 ? 'left' : 'right'} delay={i * 0.2}>
              <div className="group bg-stone-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-stone-100">
                <div className="relative overflow-hidden h-64">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-5 left-5">
                    <span className={`bg-${p.color}-500 text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                      {p.subtitle}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <div className={`w-12 h-12 bg-${p.color}-100 rounded-xl flex items-center justify-center mb-5`}>
                    <i className={`${p.icon} text-2xl text-${p.color}-600`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-stone-800 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {p.title}
                  </h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            </AnimatedBlock>
          ))}
        </div>

        {/* 우리가 믿는 것 */}
        <AnimatedBlock direction="up" delay={0}>
          <div className="mb-10">
            <span className="text-emerald-600 text-sm font-semibold tracking-widest uppercase">Chapter 04</span>
            <h2
              className="text-5xl font-bold text-stone-800 mt-3 mb-6 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              우리가 믿는 것
            </h2>
            <div className="w-16 h-0.5 bg-emerald-500 mb-12"></div>
          </div>
        </AnimatedBlock>

        <div className="grid md:grid-cols-3 gap-8 mb-32">
          {beliefs.map((b, i) => (
            <AnimatedBlock key={i} direction="up" delay={i * 0.15}>
              <div className="bg-stone-50 rounded-2xl p-8 border border-stone-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default h-full">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${b.icon} text-2xl text-emerald-600`}></i>
                </div>
                <p className="text-stone-700 text-base leading-relaxed font-medium" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                  {b.text}
                </p>
              </div>
            </AnimatedBlock>
          ))}
        </div>

        {/* 마무리 클로징 섹션 */}
        <div
          ref={closingRef}
          className="relative rounded-3xl overflow-hidden"
          style={{
            opacity: closingVisible ? 1 : 0,
            transform: closingVisible ? 'translateY(0)' : 'translateY(50px)',
            transition: 'all 1s ease',
          }}
        >
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20artisan%20hands%20sewing%20on%20machine%20close%20up%20warm%20golden%20light%20Korean%20workshop%20table%20fabric%20thread%20needle%20craftsmanship%20sustainable%20fashion%20production%20authentic%20moment%20beautiful%20texture%20natural%20tones%20Korea%20sewing&width=1400&height=700&seq=daygreen-closing-korean-v1&orientation=landscape"
            alt="Daygreen 마무리"
            className="w-full h-[500px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-16">
            <p className="text-white/70 text-sm tracking-widest uppercase mb-4">Our Beginning</p>
            <h3
              className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              우리의 시작은
              <br />
              여전히 같습니다
            </h3>
            <p className="text-white/80 text-base leading-relaxed max-w-lg mb-8" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
              재봉틀 위에서, 사람의 손으로,
              <br />
              한 벌의 제품이 만들어지는 순간.
              <br />
              그 작은 기술의 움직임이 공동체를 만들고
              <br />
              지속가능한 미래로 이어집니다.
            </p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-px bg-emerald-400"></div>
              <p className="text-emerald-300 font-semibold text-sm tracking-wider" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                기술로 자립하고, 존중으로 지속된다. — Daygreen
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
